import { useState, useEffect, useCallback } from "react";

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export const PWA_DISMISSED_STORAGE_KEY = "pwa_install_banner_dismissed";
export const PWA_DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export function recordPwaDismissal(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
        window.localStorage.setItem(PWA_DISMISSED_STORAGE_KEY, Date.now().toString());
    } catch {
        // Silently ignore storage quota or private mode issues
    }
}

export function isPwaDismissed(): boolean {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
        const dismissedAt = window.localStorage.getItem(PWA_DISMISSED_STORAGE_KEY);
        if (!dismissedAt) return false;
        const timeSinceDismissal = Date.now() - parseInt(dismissedAt, 10);
        return timeSinceDismissal < PWA_DISMISS_DURATION_MS;
    } catch {
        return false;
    }
}

export function checkIsStandalone(): boolean {
    if (typeof window === "undefined") return false;
    const isDisplayStandalone =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(display-mode: standalone)").matches;
    const isNavigatorStandalone =
        "standalone" in window.navigator &&
        (window.navigator as unknown as { standalone: boolean }).standalone === true;
    return Boolean(isDisplayStandalone || isNavigatorStandalone);
}

export function checkIsIos(): boolean {
    if (typeof window === "undefined") return false;
    const userAgent = window.navigator.userAgent.toLowerCase();
    // Strictly target iPhone and iPod; iPads are treated as desktop/tablet responsive web
    return /iphone|ipod/.test(userAgent) && !("MSStream" in window);
}

export function checkIsMobileViewport(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 767px)").matches;
}

// Module-level capture so beforeinstallprompt is never dropped if fired before React mounts
let capturedPromptEvent: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<(event: BeforeInstallPromptEvent | null) => void>();

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        capturedPromptEvent = e as BeforeInstallPromptEvent;
        promptListeners.forEach((listener) => listener(capturedPromptEvent));
    });

    window.addEventListener("appinstalled", () => {
        capturedPromptEvent = null;
        promptListeners.forEach((listener) => listener(null));
    });
}

// Test helper to simulate or reset captured event
export function _setCapturedPromptForTesting(event: BeforeInstallPromptEvent | null): void {
    capturedPromptEvent = event;
    promptListeners.forEach((listener) => listener(capturedPromptEvent));
}

export function usePwaInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
        () => capturedPromptEvent
    );
    const [isStandalone, setIsStandalone] = useState(checkIsStandalone);
    const [isIos] = useState(checkIsIos);
    const [isMobile, setIsMobile] = useState(checkIsMobileViewport);
    const [isInstalled, setIsInstalled] = useState(checkIsStandalone);

    useEffect(() => {
        // Subscribe to module-level prompt capture
        const promptListener = (event: BeforeInstallPromptEvent | null) => {
            setDeferredPrompt(event);
        };
        promptListeners.add(promptListener);

        // Listen for Chromium beforeinstallprompt directly as well
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            capturedPromptEvent = e as BeforeInstallPromptEvent;
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        // Listen for appinstalled
        const handleAppInstalled = () => {
            capturedPromptEvent = null;
            setIsInstalled(true);
            setDeferredPrompt(null);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);

        // Listen for mobile viewport changes (< 768px)
        let mobileMediaQuery: MediaQueryList | null = null;
        let handleViewportChange: ((e: MediaQueryListEvent) => void) | null = null;

        if (typeof window.matchMedia === "function") {
            mobileMediaQuery = window.matchMedia("(max-width: 767px)");
            if (mobileMediaQuery) {
                handleViewportChange = (e: MediaQueryListEvent) => {
                    setIsMobile(e.matches);
                };
                if (mobileMediaQuery.addEventListener) {
                    mobileMediaQuery.addEventListener("change", handleViewportChange);
                }
            }
        }

        // Listen for standalone display-mode changes
        let mediaQuery: MediaQueryList | null = null;
        let handleMediaChange: ((e: MediaQueryListEvent) => void) | null = null;

        if (typeof window.matchMedia === "function") {
            mediaQuery = window.matchMedia("(display-mode: standalone)");
            if (mediaQuery) {
                handleMediaChange = (e: MediaQueryListEvent) => {
                    setIsStandalone(e.matches);
                    if (e.matches) {
                        setIsInstalled(true);
                    }
                };

                if (mediaQuery.addEventListener) {
                    mediaQuery.addEventListener("change", handleMediaChange);
                }
            }
        }

        return () => {
            promptListeners.delete(promptListener);
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
            if (mobileMediaQuery && handleViewportChange && mobileMediaQuery.removeEventListener) {
                mobileMediaQuery.removeEventListener("change", handleViewportChange);
            }
            if (mediaQuery && handleMediaChange && mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", handleMediaChange);
            }
        };
    }, []);

    const triggerInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "manual-instructions"> => {
        if (deferredPrompt) {
            await deferredPrompt.prompt();
            const choiceResult = await deferredPrompt.userChoice;
            capturedPromptEvent = null;
            setDeferredPrompt(null);
            if (choiceResult.outcome === "accepted") {
                setIsInstalled(true);
            }
            return choiceResult.outcome;
        }

        // On iOS or browsers without beforeinstallprompt, manual instructions are needed
        return "manual-instructions";
    }, [deferredPrompt]);

    // Install option is strictly gated to mobile viewports (< 768px) and not already installed
    const canInstall =
        isMobile &&
        !isStandalone &&
        !isInstalled &&
        (Boolean(deferredPrompt) || isIos);

    return {
        isStandalone,
        isInstalled,
        isIos,
        isMobile,
        canInstall,
        hasNativePrompt: Boolean(deferredPrompt),
        triggerInstall,
    };
}
